import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { LoadingSpinner } from '@/shared/components';

export default function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorTimeout, setErrorTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showError, setShowError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const { login, isAuthenticated, user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if user just registered
  useEffect(() => {
    const registered = searchParams.get('registered');
    if (registered === 'true') {
      setRegistrationSuccess(true);
      // Clean up the URL parameter
      navigate('/login', { replace: true });
    }
  }, [searchParams, navigate]);

  // Initialize error state from localStorage on component mount
  useEffect(() => {
    const savedError = localStorage.getItem('loginError');
    if (savedError) {
      setError(savedError);
      setShowError(true);
      localStorage.removeItem('loginError'); // Clear after reading
    }
  }, []);

  // Handle session conflict notification from URL params
  useEffect(() => {
    const sessionConflict = searchParams.get('sessionConflict');
    if (sessionConflict === 'true') {
      // Don't show notification here - let AuthContext handle it to avoid duplicates
      // Just clean up the URL parameter
      navigate('/login', { replace: true });
    }
  }, [searchParams, navigate]);

  // Redirect authenticated users to their dashboard
  useEffect(() => {
    if (isAuthenticated && user && !loading) {
      // Check if user is a referral recruit who needs to complete onboarding
      // Only applies to users with accountStatus === 'onboarding'
      const isReferralRecruit = user.accountStatus === 'onboarding';
      const onboardingStep = (user as any).onboardingStep;

      if (isReferralRecruit && onboardingStep >= 3 && onboardingStep <= 6) {
        navigate(`/onboarding/step-${onboardingStep}`);
        return;
      }

      switch (user.role) {
        case 'agent':
          navigate('/agent/dashboard');
          break;
        case 'hr_manager':
          navigate('/hr/dashboard');
          break;
        case 'fmo_ceo':
          navigate('/fmo/dashboard');
          break;
        case 'sales_manager':
          navigate('/sales/dashboard');
          break;
        case 'user':
          navigate('/user/dashboard');
          break;
        default:
          navigate('/dashboard');
      }
    }
  }, [isAuthenticated, user, loading, navigate]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeout) {
        clearTimeout(errorTimeout);
      }
    };
  }, [errorTimeout]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    );
  }

  // Redirect if already authenticated
  if (isAuthenticated && user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear any existing timeout first
    if (errorTimeout) {
      clearTimeout(errorTimeout);
      setErrorTimeout(null);
    }

    setError('');
    setShowError(false);
    localStorage.removeItem('loginError');

    // Basic validation
    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password.');
      setShowError(true);
      return;
    }

    setIsLoading(true);

    try {
      await login({ email: email.trim(), password });
      // Navigation will be handled by the useEffect above
    } catch (err: any) {
      console.error('Login error:', err);
      // Extract error message from different possible error structures
      let errorMessage = 'Login failed. Please try again.';

      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setShowError(true);
      // Save error to localStorage as backup
      localStorage.setItem('loginError', errorMessage);

      // Set a longer timeout to auto-clear error (optional - for better UX)
      const timeout = setTimeout(() => {
        setError('');
        setShowError(false);
        localStorage.removeItem('loginError');
      }, 15000); // 15 seconds
      setErrorTimeout(timeout);
    } finally {
      setIsLoading(false);
    }
  };

  // Only show demo credentials in development
  // Check both custom environment variable and NODE_ENV
  const isProduction = process.env.REACT_APP_IS_PRODUCTION === 'true';

  const demoCredentials = [
    { role: 'Sales Manager', email: 'manager@example.com', password: 'password123' },
    { role: 'Sales Rep', email: 'agent1@example.com', password: 'password123' },
    { role: 'Sales Rep', email: 'salesrep-test@example.com', password: 'password123' },
    { role: 'HR Manager', email: 'hr@example.com', password: 'password123' },
    { role: 'FMO CEO', email: 'fmo@example.com', password: 'password123' },
  ];

  const fillDemoCredentials = (email: string, password: string) => {
    setEmail(email);
    setPassword(password);
    setError(''); // Clear any existing errors when filling demo credentials
    setShowError(false);
    localStorage.removeItem('loginError');
    // Clear timeout when manually clearing error
    if (errorTimeout) {
      clearTimeout(errorTimeout);
      setErrorTimeout(null);
    }
  };

  const handleInputChange = (field: 'email' | 'password', value: string) => {
    if (field === 'email') {
      setEmail(value);
    } else {
      setPassword(value);
    }
    // Only clear error when user makes a meaningful change (not just focus/blur)
    // We'll clear it on form submission instead
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6 sm:space-y-8 p-6 sm:p-8 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-accent-900">Sign In</h2>
          <p className="mt-2 text-sm sm:text-base text-accent-600">
            Welcome back! Please sign in to your account.
          </p>

          {/* Single Session Notice */}
          <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl shadow-sm">
            <div className="flex items-start text-left">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-semibold text-amber-800 mb-1 text-left">
                  Security Notice
                </h3>
                <p className="text-sm text-amber-700 leading-relaxed text-left">
                  Logging in will automatically end any active sessions on other devices to ensure account security.
                </p>
              </div>
            </div>
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {registrationSuccess && (
            <div className="bg-green-50 border-l-4 border-green-400 text-green-700 px-4 py-3 rounded-md shadow-sm">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">Account Created Successfully!</span>
              </div>
              <p className="mt-1 text-sm">Please sign in to complete your onboarding.</p>
            </div>
          )}

          {showError && error && (
            <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded-md shadow-sm animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">Login Failed</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setError('');
                    setShowError(false);
                    localStorage.removeItem('loginError');
                    if (errorTimeout) {
                      clearTimeout(errorTimeout);
                      setErrorTimeout(null);
                    }
                  }}
                  className="text-red-400 hover:text-red-600 focus:outline-none focus:text-red-600 transition-colors"
                  title="Dismiss error message"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <p className="mt-2 text-sm font-medium">{error}</p>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-accent-700">
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-accent-300 rounded-md shadow-sm placeholder-accent-400 focus:outline-none focus:ring-accent-500 focus:border-accent-500"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-accent-700">
              Password
            </label>
            <div className="mt-1 relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="block w-full px-3 py-2 pr-10 border border-accent-300 rounded-md shadow-sm placeholder-accent-400 focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-accent-400 hover:text-accent-600 focus:outline-none focus:text-accent-600 transition-colors"
                tabIndex={-1}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Forgot Password Link */}
          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              Forgot your password?
            </Link>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading || loading}
              className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <LoadingSpinner size="sm" text="Signing in..." />
              ) : (
                'Sign In'
              )}
            </button>
          </div>
        </form>

        {/* Demo Credentials - Only show in development */}
        {!isProduction && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-3 text-center">Demo Credentials:</p>
            <div className="space-y-2">
              {demoCredentials.map((cred, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => fillDemoCredentials(cred.email, cred.password)}
                  className="w-full text-left p-2 text-xs bg-gray-50 hover:bg-gray-100 rounded border transition-colors"
                >
                  <span className="font-medium">{cred.role}:</span> {cred.email} / {cred.password}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
